import * as T from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import type { Quality } from './quality';

/**
 * Final image: HDR scene → bloom → filmic tone map, colour grade, speed blur,
 * lens fringing, vignette and grain in one pass → optional FXAA.
 */
const FinalShader = {
  uniforms: {
    tDiffuse: { value: null as T.Texture | null },
    exposure: { value: 1 }, time: { value: 0 }, speedBlur: { value: 0 }, focus: { value: new T.Vector2(.5, .56) },
    vignette: { value: .3 }, grain: { value: .016 }, aberration: { value: .0007 },
    saturation: { value: 1.12 }, contrast: { value: 1.07 },
    shadowTint: { value: new T.Vector3(.985, 1, 1.03) }, highlightTint: { value: new T.Vector3(1.03, 1.005, .97) },
    flash: { value: 0 },
  },
  vertexShader: /* glsl */`varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }`,
  fragmentShader: /* glsl */`
uniform sampler2D tDiffuse; uniform float exposure; uniform float time; uniform float speedBlur; uniform vec2 focus;
uniform float vignette; uniform float grain; uniform float aberration; uniform float saturation; uniform float contrast;
uniform vec3 shadowTint; uniform vec3 highlightTint; uniform float flash;
varying vec2 vUv;
vec3 tqRRTFit( vec3 v ) { vec3 a = v * ( v + 0.0245786 ) - 0.000090537; vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081; return a / b; }
vec3 tqAces( vec3 color ) {
  const mat3 inputMat = mat3( vec3( 0.59719, 0.07600, 0.02840 ), vec3( 0.35458, 0.90834, 0.13383 ), vec3( 0.04823, 0.01566, 0.83777 ) );
  const mat3 outputMat = mat3( vec3( 1.60475, -0.10208, -0.00327 ), vec3( -0.53108, 1.10813, -0.07276 ), vec3( -0.07367, -0.00605, 1.07602 ) );
  color *= exposure / 0.6; color = inputMat * color; color = tqRRTFit( color ); return clamp( outputMat * color, 0.0, 1.0 );
}
vec3 tqToSRGB( vec3 c ) { return mix( pow( c, vec3( 0.41666 ) ) * 1.055 - vec3( 0.055 ), c * 12.92, vec3( lessThanEqual( c, vec3( 0.0031308 ) ) ) ); }
float tqRand( vec2 co ) { return fract( sin( dot( co, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 ); }
void main() {
  vec2 toFocus = vUv - focus; float r = length( toFocus );
  vec3 hdr;
  float blur = speedBlur * smoothstep( 0.12, 0.75, r );
  if ( blur > 0.0005 ) {
    vec3 acc = vec3( 0.0 ); float w = 0.0;
    for ( int i = 0; i < 10; i++ ) { float t = float( i ) / 9.0; float k = 1.0 - t * 0.6; acc += texture2D( tDiffuse, vUv - toFocus * blur * t ).rgb * k; w += k; }
    hdr = acc / w;
  } else hdr = texture2D( tDiffuse, vUv ).rgb;
  // Lateral chromatic aberration toward the frame edges.
  vec2 ca = toFocus * aberration * r * 4.0;
  hdr.r = mix( hdr.r, texture2D( tDiffuse, vUv - ca ).r, 0.85 );
  hdr.b = mix( hdr.b, texture2D( tDiffuse, vUv + ca ).b, 0.85 );
  vec3 c = tqAces( hdr );
  float luma = dot( c, vec3( 0.2126, 0.7152, 0.0722 ) );
  c = mix( vec3( luma ), c, saturation );
  c = mix( c * shadowTint, c * highlightTint, smoothstep( 0.15, 0.85, luma ) );
  c = ( c - 0.5 ) * contrast + 0.5;
  c = max( c, 0.0 );
  vec2 v = ( vUv - 0.5 ) * vec2( 1.1, 1.0 );
  c *= mix( 1.0, smoothstep( 0.95, 0.25, length( v ) ), vignette );
  c = mix( c, vec3( 1.0 ), flash );
  c = tqToSRGB( clamp( c, 0.0, 1.0 ) );
  c += ( tqRand( vUv * 1000.0 + fract( time ) ) - 0.5 ) * grain;
  gl_FragColor = vec4( c, 1.0 );
}`,
};

export interface PostParams { speedBlur: number; flash: number; time: number }
export class PostFX {
  composer: EffectComposer; final: ShaderPass; bloom: UnrealBloomPass | null = null; fxaa: ShaderPass | null = null;
  constructor(renderer: T.WebGLRenderer, scene: T.Scene, camera: T.Camera, quality: Quality) {
    const size = renderer.getDrawingBufferSize(new T.Vector2());
    const target = new T.WebGLRenderTarget(size.x, size.y, { type: T.HalfFloatType, samples: quality.msaa, depthBuffer: true });
    this.composer = new EffectComposer(renderer, target);
    this.composer.addPass(new RenderPass(scene, camera));
    if (quality.bloom) { this.bloom = new UnrealBloomPass(new T.Vector2(size.x / 2, size.y / 2), .28, .6, 3.6); this.composer.addPass(this.bloom); }
    this.final = new ShaderPass(FinalShader); this.composer.addPass(this.final);
    if (!quality.msaa) { this.fxaa = new ShaderPass(FXAAShader); this.composer.addPass(this.fxaa); }
  }
  setSize(width: number, height: number, pixelRatio: number) {
    this.composer.setPixelRatio(pixelRatio); this.composer.setSize(width, height);
    if (this.fxaa) this.fxaa.material.uniforms.resolution.value.set(1 / (width * pixelRatio), 1 / (height * pixelRatio));
  }
  grade(style: { exposure: number; saturation?: number; contrast?: number }) {
    const u = this.final.uniforms; u.exposure.value = style.exposure;
    if (style.saturation) u.saturation.value = style.saturation; if (style.contrast) u.contrast.value = style.contrast;
  }
  render(params: PostParams, dt: number) {
    const u = this.final.uniforms; u.speedBlur.value = params.speedBlur; u.flash.value = params.flash; u.time.value = params.time;
    this.composer.render(dt);
  }
  dispose() { this.composer.dispose(); this.bloom?.dispose(); }
}
